import React, { useRef, useEffect, useState } from 'react';
import { func, string } from 'prop-types';
import { Form as FinalForm, Field } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';
import { intlShape, injectIntl } from '../../util/reactIntl';
import {
  Button,
  Form,
  LocationAutocompleteInput,
  Icons,
  FieldDateRangeController,
  IconSearch,
} from '../../components';
import { compose } from 'redux';
import { withViewport } from '../../util/uiHelpers';
import { getStartOf } from '../../util/dates';

import css from './LandingSearchForm.module.css';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';

const identity = v => v;

const ChevronDownIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`lucide lucide-chevron-down h-3.5 w-3.5 text-muted-foreground ${css.chevronIcon}`}
    aria-hidden="true"
  >
    <path d="m6 9 6 6 6-6"></path>
  </svg>
);

const LocationSearchField = props => {
  const { intl, handleChange, isLandingLocation, isSearchPageFilter, locationRef } = props;
  const locationLabel = intl.formatMessage({
    id: 'LandingSearchForm.locationLabel',
    defaultMessage: 'Location',
  });
  const locationPlaceholder = intl.formatMessage({
    id: 'LandingSearchForm.locationPlaceholder',
    defaultMessage: 'Select a location',
  });

  return (
    <Field
      name="location"
      format={identity}
      render={({ input, meta }) => {
        const { onChange, ...restInput } = input;

        const searchOnChange = value => {
          onChange(value);
          handleChange(value);
        };

        const searchInput = { ...restInput, onChange: searchOnChange };
        return (
          <div className={css.locationFld} ref={locationRef}>
            <label>{locationLabel}</label>
            <div className={css.controlWithChevron}>
              <LocationAutocompleteInput
                expandSearchArea
                placeholder={locationPlaceholder}
                iconClassName={css.searchInputIcon}
                inputClassName={css.searchInput}
                predictionsClassName={css.searchPredictions}
                input={searchInput}
                meta={meta}
                rootClassName={isLandingLocation && css.landingLocationSearch}
                isLandingLocation={isLandingLocation}
                isSearchPageFilter={isSearchPageFilter}
                locationRef={locationRef}
              />
              <ChevronDownIcon />
            </div>
          </div>
        );
      }}
    />
  );
};

const KeywordSearchField = props => {
  const { intl, isSearchPageFilter, isTopbarSearch } = props;
  const keywordInputRef = useRef(null);
  const inputId = isTopbarSearch
    ? 'topbarKeywordSearchInput'
    : isSearchPageFilter
    ? 'filterKeywordSearchInput'
    : 'heroKeywordSearchInput';
  const keywordPlaceholder = intl.formatMessage({
    id: 'LandingSearchForm.keywordPlaceholder',
    defaultMessage: 'Type a location keyword',
  });

  return (
    <Field
      name="keywords"
      format={value => value || ''}
      parse={value => value}
      render={({ input, meta }) => {
        const focusKeywordInput = event => {
          event.stopPropagation();
          keywordInputRef.current?.focus();
        };

        return (
          <div
            className={classNames(css.keywordSearch, {
              [css.searchPageFilter]: isSearchPageFilter,
              [css.topbarSearchFilter]: isTopbarSearch,
            })}
            onClick={focusKeywordInput}
          >
            <label htmlFor={inputId}>
              {intl.formatMessage({
                id: 'LandingSearchForm.keywordLabel',
                defaultMessage: 'Keyword Search',
              })}
            </label>

            <div className={css.keywordInputWrapper}>
              <IconSearch className={css.keywordSearchIcon} />
              <input
                {...input}
                ref={keywordInputRef}
                id={inputId}
                type="text"
                autoComplete="off"
                placeholder={keywordPlaceholder}
                className={css.keywordInput}
              />
              {meta.touched && meta.error && <span className={css.error}>{meta.error}</span>}
            </div>
          </div>
        );
      }}
    />
  );
};

const LandingSearchFormComponent = props => {
  const today = new Date();
  const tomorrow = new Date(today);
  const datePickerRef = useRef(null);
  const locationRef = useRef(null);
  tomorrow.setDate(today.getDate() + 1);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const dateFormattingOptions = { month: 'short', day: 'numeric' };
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const isOutsideRange = day => {
    const localizedDay = getStartOf(day, 'day', timeZone);
    const todayStart = getStartOf(today, 'day', timeZone);
    return localizedDay < todayStart;
  };

  const handleClearDates = form => {
    form.change('dates', null);
  };

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        isCalendarOpen &&
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setIsCalendarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCalendarOpen]);

  return (
    <FinalForm
      {...props}
      mutators={{ ...arrayMutators }}
      render={formRenderProps => {
        const {
          rootClassName,
          className,
          values,
          intl,
          form,
          isSearchPageFilter,
          isTopbarSearch,
          showTopbarSearch,
          showSecondDiv,
          handleSubmit,
        } = formRenderProps;

        const location = useLocation();
        const isLandingPage = location.pathname === '/';

        const classes = classNames(rootClassName || css.root, className, {
          [css.searchPageFilter]: isSearchPageFilter,
          [css.topbarSearchForm]: isTopbarSearch,
          [css.showTopbarSearch]: !isLandingPage || showTopbarSearch || showSecondDiv,
        });

        const hasDates = values.dates && (values.dates.startDate || values.dates.endDate);
        const dateInputId = isTopbarSearch ? 'topbarDateSearch' : 'heroDateSearch';

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            <div className={css.formBody}>
              <KeywordSearchField
                intl={intl}
                isSearchPageFilter={isSearchPageFilter}
                isTopbarSearch={isTopbarSearch}
              />
              <span className={css.lineSeparator}></span>

              <LocationSearchField
                intl={intl}
                locationRef={locationRef}
                handleChange={location => {
                  if (location.selectedPlace) {
                    form.change('location', location);
                  }
                }}
                isLandingLocation={true}
                isSearchPageFilter={isSearchPageFilter}
                isTopbarSearch={isTopbarSearch}
              />

              <span className={css.lineSeparator}></span>
              <div className={css.dateRangePickerContainer}>
                <label htmlFor={dateInputId}>
                  {intl.formatMessage({ id: 'LandingSearchForm.date' })}
                </label>
                <div className={css.dateRangePickerWrapper} ref={datePickerRef}>
                  <Field
                    name="dates"
                    render={({ input: { value, onChange }, meta }) => {
                      const handleDateChange = selectedDate => {
                        if (selectedDate) {
                          const startDate = selectedDate;
                          const endDate = new Date(selectedDate);
                          endDate.setDate(startDate.getDate() + 1);

                          onChange({ startDate, endDate });
                          setIsCalendarOpen(false);
                        }
                      };

                      const selectedStartDate = value?.startDate || null;
                      const dateInputText = selectedStartDate
                        ? intl.formatDate(selectedStartDate, dateFormattingOptions)
                        : '';

                      return (
                        <div
                          className={css.customDateField}
                          onClick={() => setIsCalendarOpen(true)}
                        >
                          <Icons name="calendar" />
                          <input
                            id={dateInputId}
                            type="text"
                            value={dateInputText}
                            placeholder={intl.formatMessage({
                              id: 'LandingSearchForm.datePlaceholder',
                            })}
                            readOnly
                            className={css.dateInput}
                            onClick={() => setIsCalendarOpen(true)}
                          />
                          {isCalendarOpen && (
                            <div
                              className={css.calendarOverlay}
                              onClick={event => event.stopPropagation()}
                            >
                              <FieldDateRangeController
                                name="dates"
                                minimumNights={0}
                                isDaily={true}
                                isOpen={isCalendarOpen}
                                onChange={selectedDates => {
                                  // Single date selected
                                  if (selectedDates && selectedDates.startDate) {
                                    handleDateChange(selectedDates.startDate);
                                  }
                                }}
                                onClose={() => setIsCalendarOpen(false)}
                                isOutsideRange={isOutsideRange}
                                useMobileMargins
                                isSingleDatePicker
                              />
                            </div>
                          )}
                        </div>
                      );
                    }}
                  />
                  <ChevronDownIcon />

                  {hasDates && (
                    <button
                      className={css.clearButton}
                      onClick={() => handleClearDates(form)}
                      type="button"
                      aria-label={intl.formatMessage({
                        id: 'LandingSearchForm.clearDates',
                        defaultMessage: 'Clear dates',
                      })}
                    >
                      <Icons name="close" />
                    </button>
                  )}
                </div>
              </div>

              <Button
                className={classNames({
                  [css.searchIcon]: isSearchPageFilter,
                  [css.searchBtn]: !isSearchPageFilter,
                })}
                type="submit"
              >
                {intl.formatMessage({ id: 'LandingSearchForm.search' })}
              </Button>
            </div>
          </Form>
        );
      }}
    />
  );
};

LandingSearchFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  onSubmit: func.isRequired,
  intl: intlShape.isRequired,
};

const LandingSearchForm = compose(
  withViewport,
  injectIntl
)(LandingSearchFormComponent);

export default LandingSearchForm;
